import { useMemo } from "react";
import { useSelector } from "react-redux";
import * as S from "../dashboard/_shared";
import SegSection from "../segSection";
import {
  normalizeText,
  ordenarPeriodo as ordenarPeriodoPura,
  buildSegmentationHistoryData as buildSegmentationHistoryDataPura,
} from "./dataTransforms";
import "./style.scss";

export default function SegmentationView({ ctx }) {
  const segmentationState = useSelector(
    (state) => state.dashboard.segmentation,
  );
  const segmentationData = segmentationState?.data?.data || [];
  const segmentationHistoryState = useSelector(
    (state) => state.dashboard.segmentationHistory,
  );
  const segmentationHistoryDataRaw =
    segmentationHistoryState?.data?.data || [];
  const selectedCity = useSelector((state) =>
    state.filters.city && state.filters.city !== "todas" ? state.filters.city : null,
  );

  // fetchSegmentationHistory traz TODOS os tipos de período juntos (mensal,
  // trimestral, semestral, anual) — sem isso os gráficos de série histórica
  // misturam pontos de granularidades diferentes no mesmo eixo X. Cruza
  // period_name com state.dashboard.periods (mesma fonte do PeriodPillBig
  // global, que já tem o campo .type) pra manter só o tipo do período
  // atualmente selecionado no filtro global.
  const periodsState = useSelector((state) => state.dashboard.periods);
  const selectedPeriodName = useSelector((state) => state.filters.period);
  const periodMetaByName = useMemo(() => {
    const rows = periodsState?.data?.data;
    if (!Array.isArray(rows)) return {};
    return rows.reduce((acc, p) => {
      if (p.name && !acc[p.name]) {
        acc[p.name] = { type: p.type, start_date: p.start_date };
      }
      return acc;
    }, {});
  }, [periodsState]);
  const periodTypeByName = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(periodMetaByName).map(([name, meta]) => [
          name,
          meta.type,
        ]),
      ),
    [periodMetaByName],
  );
  const activePeriodType = periodTypeByName[selectedPeriodName];
  const segmentationHistoryData = useMemo(() => {
    if (!activePeriodType) return segmentationHistoryDataRaw;
    return segmentationHistoryDataRaw.filter(
      (item) => periodTypeByName[item.period_name] === activePeriodType,
    );
  }, [segmentationHistoryDataRaw, periodTypeByName, activePeriodType]);

  if (
    process.env.NODE_ENV !== "production" &&
    (segmentationData.length > 0 || segmentationHistoryData.length > 0)
  ) {
    const allData = [...segmentationData, ...segmentationHistoryData];
    const uniqueTypes = [...new Set(allData.map((d) => d.type_description))];
    console.debug(
      "[SegmentationView] type_description values in API response:",
      uniqueTypes,
    );
  }
  const overviewState = useSelector((state) => state.dashboard.overview);
  const overviewData = overviewState?.data?.data || [];

  const metas = {
    nps: overviewData[0]?.nps_target,
    csat: overviewData[0]?.csat_target,
    ces: overviewData[0]?.ces_target,
    jcx: overviewData[0]?.cx_target,
  };

  const { C, ESCALA, F, JCFG, PeriodPill, TT, UNIMED, Zonas } = S;
  const {
    hiddenLines,
    pS,
    segHistMode,
    segIndicador,
    setHiddenLines,
    setPS,
    setSegIndicador,
    toggleLine,
    toggleSegHist,
  } = ctx;

  return (() => {
    // KPIs consolidados
    const nps = overviewData[0]?.nps || 0;
    const csat = overviewData[0]?.csat || 0;
    const ces = overviewData[0]?.ces || 0;

    const GENERATION_TYPE_DESCRIPTIONS = new Set([
      "faixa etaria",
      "generation",
      "age range",
    ]);

    function isGenerationType(typeDescription) {
      return GENERATION_TYPE_DESCRIPTIONS.has(normalizeText(typeDescription));
    }

    const generationLabelMap = {
      "gen z": "Gen Z",
      "gen y": "Gen Y",
      millennial: "Gen Y",
      "gen x": "Gen X",
      boomer: "Baby Boomers",
      "baby boomer": "Baby Boomers",
      "baby boomers": "Baby Boomers",
      "baby bommers": "Baby Boomers",
      aposentado: "Aposentado",
    };

    const generationRangeMap = {
      "Gen Z": "1997–2012",
      "Gen Y": "1981–1996",
      "Gen X": "1965–1980",
      "Baby Boomers": "1946–1964",
      Aposentado: "1946–1964",
    };

    const generationSortOrder = {
      "Gen Z": 0,
      "Gen Y": 1,
      "Gen X": 2,
      "Baby Boomers": 3,
    };

    function toTitleCase(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
    }

    function ordenarPeriodo(periodo) {
      return ordenarPeriodoPura(periodo, periodMetaByName);
    }

    function buildSegmentationHistoryData(category) {
      return buildSegmentationHistoryDataPura(
        segmentationHistoryData,
        category,
        periodMetaByName,
      );
    }

    function buildGeracoesHistData(category) {
      const grouped = new Map();
      segmentationHistoryData
        .filter(
          (item) =>
            item.category === category &&
            isGenerationType(item.type_description),
        )
        .forEach((item) => {
          const fullPeriod = item.period_name;
          const normalized = normalizeText(item.description);
          const label =
            generationLabelMap[normalized] || toTitleCase(item.description);
          const row = grouped.get(fullPeriod) || {
            p: fullPeriod.split("/")[0],
            _ord: ordenarPeriodo(fullPeriod),
          };
          row[label] = Math.round(Number(item.value) || 0);
          grouped.set(fullPeriod, row);
        });
      return Array.from(grouped.values())
        .sort((a, b) => a._ord - b._ord)
        .map(({ _ord, ...row }) => row);
    }

    function buildOrigemHistData(category) {
      const grouped = new Map();
      segmentationHistoryData
        .filter(
          (item) =>
            item.category === category && isOrigemType(item.type_description),
        )
        .forEach((item) => {
          const fullPeriod = item.period_name;
          const lbl =
            normalizeText(item.description) === "local"
              ? "Local"
              : normalizeText(item.description) === "intercambio"
                ? "Intercâmbio"
                : toTitleCase(item.description);
          const row = grouped.get(fullPeriod) || {
            p: fullPeriod.split("/")[0],
            _ord: ordenarPeriodo(fullPeriod),
          };
          row[lbl] = Math.round(Number(item.value) || 0);
          grouped.set(fullPeriod, row);
        });
      return Array.from(grouped.values())
        .sort((a, b) => a._ord - b._ord)
        .map(({ _ord, ...row }) => row);
    }

    function buildCidadesHistData(category) {
      const grouped = new Map();
      segmentationHistoryData
        .filter((item) => {
          const td = normalizeText(item.type_description);
          return (
            item.category === category &&
            (td === "city" ||
              td === "cidade" ||
              td === "municipio" ||
              td.startsWith("city") ||
              td.startsWith("cidad"))
          );
        })
        .forEach((item) => {
          const fullPeriod = item.period_name;
          const nome = item.description
            .split(" ")
            .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
            .join(" ");
          const row = grouped.get(fullPeriod) || {
            p: fullPeriod.split("/")[0],
            _ord: ordenarPeriodo(fullPeriod),
          };
          row[nome] = item.value;
          grouped.set(fullPeriod, row);
        });
      return Array.from(grouped.values())
        .sort((a, b) => a._ord - b._ord)
        .map(({ _ord, ...row }) => row);
    }

    function buildGeracoesData(category) {
      return segmentationData
        .filter(
          (item) =>
            item.category === category &&
            isGenerationType(item.type_description),
        )
        .map((item) => {
          const normalized = normalizeText(item.description);
          const label =
            generationLabelMap[normalized] || toTitleCase(item.description);
          return {
            g: label,
            range: generationRangeMap[label] || "",
            n: Math.round(Number(item.value) || 0),
            resp: Number(item.mentions_count || 0),
          };
        })
        .sort(
          (a, b) =>
            (generationSortOrder[a.g] ?? Number.MAX_SAFE_INTEGER) -
            (generationSortOrder[b.g] ?? Number.MAX_SAFE_INTEGER),
        );
    }

    function isOrigemType(td) {
      const n = normalizeText(td);
      return n.includes("unimed_client");
    }

    function buildOrigemData(category) {
      const ORDER = { Local: 0, Intercâmbio: 1 };
      return segmentationData
        .filter(
          (item) =>
            item.category === category && isOrigemType(item.type_description),
        )
        .map((item) => {
          const label = normalizeText(item.description);
          return {
            t:
              label === "local"
                ? "Local"
                : label === "intercambio"
                  ? "Intercâmbio"
                  : toTitleCase(item.description),
            n: Math.round(Number(item.value) || 0),
            resp: Number(item.mentions_count || 0),
          };
        })
        .sort((a, b) => (ORDER[a.t] ?? 99) - (ORDER[b.t] ?? 99));
    }

    const segmentationDs = {
      segMensal: buildSegmentationHistoryData("nps"),
      segMensalCsat: buildSegmentationHistoryData("csat"),
      segMensalCes: buildSegmentationHistoryData("ces"),
      geracoes: buildGeracoesData("nps"),
      geracoesCsat: buildGeracoesData("csat"),
      geracoesCes: buildGeracoesData("ces"),
      origem: buildOrigemData("nps"),
      origemCsat: buildOrigemData("csat"),
      origemCes: buildOrigemData("ces"),
    };

    const cidadesDataToProcess = segmentationData.filter((e) => {
      const td = normalizeText(e.type_description);
      return (
        td === "city" ||
        td === "cidade" ||
        td === "municipio" ||
        td.startsWith("city") ||
        td.startsWith("cidad")
      );
    });
    let cidadesData = transformarCidades(cidadesDataToProcess);
    if (!cidadesData.length && selectedCity) {
      // City filter active: segmentationData has no city rows.
      // Fall back to history (always unfiltered), filtered to the selected city only.
      const normalizedCity = normalizeText(selectedCity);
      const cityHistItems = segmentationHistoryData.filter((e) => {
        const td = normalizeText(e.type_description);
        return (
          (td === "city" ||
            td === "cidade" ||
            td === "municipio" ||
            td.startsWith("city") ||
            td.startsWith("cidad")) &&
          normalizeText(e.description) === normalizedCity
        );
      });
      const allPeriods = [...new Set(cityHistItems.map((e) => e.period_name))];
      allPeriods.sort((a, b) => ordenarPeriodo(b) - ordenarPeriodo(a));
      const latestPeriod = allPeriods[0];
      if (latestPeriod) {
        cidadesData = transformarCidades(
          cityHistItems.filter((e) => e.period_name === latestPeriod),
        );
      }
    }

    function transformarCidades(data) {
      const resultado = {};

      data.forEach((item) => {
        const rawNome = item.description
          .split(" ")
          .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
          .join(" ");
        const nome =
          rawNome.toLowerCase() === "outras cidades"
            ? "Outras Cidades"
            : rawNome;

        // cria o objeto da cidade caso ainda nao exista
        if (!resultado[nome]) {
          resultado[nome] = {
            nome,
            nps: null,
            csat: null,
            ces: null,
            delta: item.value_delta || 0,
            n: item.mentions_count || 0,
          };
        }

        // adiciona o valor baseado na categoria
        resultado[nome][item.category] = item.value;
      });

      return Object.values(resultado);
    }

    return (
      <div
        className="tabSegmentationView"
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: C.text,
                letterSpacing: "-0.02em",
              }}
            >
              Segmentação
            </div>
            <div style={{ fontSize: 11, color: C.text4, marginTop: 2 }}>
              Selecione um indicador acima para ver suas segmentações por tipo,
              geração, origem e cidade.
            </div>
          </div>
        </div>

        {/* Seletor de indicador — 3 botões grandes */}
        <div
          style={{
            display: "flex",
            gap: 8,
            padding: "6px",
            background: C.surf3,
            borderRadius: 14,
            border: `1px solid ${C.border}`,
          }}
        >
          {[
            {
              key: "nps",
              label: "NPS",
              sub: "Lealdade",
              valor: nps,
              color: C.g700,
            },
            {
              key: "csat",
              label: "CSAT",
              sub: "Satisfação",
              valor: csat + "%",
              color: C.g600,
            },
            {
              key: "ces",
              label: "CES",
              sub: "Esforço",
              valor: ces + "%",
              color: C.blue,
            },
          ].map((opt) => {
            const active = segIndicador === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => setSegIndicador(opt.key)}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "14px 18px",
                  borderRadius: 11,
                  background: active ? "#FFFFFF" : "transparent",
                  border: active
                    ? `1.5px solid ${opt.color}`
                    : `1.5px solid transparent`,
                  cursor: "pointer",
                  transition: "all .18s",
                  boxShadow: active
                    ? `0 4px 12px ${opt.color}20, 0 2px 4px rgba(0,0,0,.04)`
                    : "none",
                  fontFamily: "inherit",
                  textAlign: "left",
                }}
              >
                <div
                  style={{
                    width: 8,
                    height: 36,
                    borderRadius: 4,
                    background: active ? opt.color : C.border,
                    transition: "all .18s",
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: active ? opt.color : C.text3,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: 3,
                    }}
                  >
                    {opt.label}
                  </div>
                  <div
                    style={{
                      fontSize: 9,
                      color: active ? C.text3 : C.text4,
                      fontWeight: 500,
                    }}
                  >
                    {opt.sub}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 700,
                    color: active ? opt.color : C.text4,
                    fontFamily: F.display,
                    letterSpacing: "-0.03em",
                    transition: "all .18s",
                  }}
                >
                  {opt.valor}
                </div>
              </button>
            );
          })}
        </div>

        {/* === Indicador selecionado: render condicional === */}
        {segIndicador === "nps" && (
          <SegSection
            label="NPS"
            color={C.g700}
            segData={segmentationDs.segMensal}
            gerData={segmentationDs.geracoes}
            origemData={segmentationDs.origem}
            meta={metas.nps}
            valorGeral={nps}
            domain={[20, 90]}
            valueSuffix=""
            showZonas={true}
            cidadesData={cidadesData}
            cidadeKey="nps"
            gerHistData={buildGeracoesHistData("nps")}
            origemHistData={buildOrigemHistData("nps")}
            cidadesHistData={buildCidadesHistData("nps")}
            segHistMode={segHistMode}
            toggleSegHist={toggleSegHist}
            hiddenLines={hiddenLines}
            setHiddenLines={setHiddenLines}
            toggleLine={toggleLine}
          />
        )}

        {segIndicador === "csat" && (
          <SegSection
            label="CSAT"
            color={C.g600}
            segData={segmentationDs.segMensalCsat}
            gerData={segmentationDs.geracoesCsat}
            origemData={segmentationDs.origemCsat}
            meta={metas.csat}
            valorGeral={csat}
            domain={[50, 95]}
            valueSuffix="%"
            showZonas={false}
            cidadesData={cidadesData}
            cidadeKey="csat"
            gerHistData={buildGeracoesHistData("csat")}
            origemHistData={buildOrigemHistData("csat")}
            cidadesHistData={buildCidadesHistData("csat")}
            segHistMode={segHistMode}
            toggleSegHist={toggleSegHist}
            hiddenLines={hiddenLines}
            setHiddenLines={setHiddenLines}
            toggleLine={toggleLine}
          />
        )}

        {segIndicador === "ces" && (
          <SegSection
            label="CES"
            color={C.blue}
            segData={segmentationDs.segMensalCes}
            gerData={segmentationDs.geracoesCes}
            origemData={segmentationDs.origemCes}
            meta={metas.ces}
            valorGeral={ces}
            domain={[20, 75]}
            valueSuffix="%"
            showZonas={false}
            cidadesData={cidadesData}
            cidadeKey="ces"
            gerHistData={buildGeracoesHistData("ces")}
            origemHistData={buildOrigemHistData("ces")}
            cidadesHistData={buildCidadesHistData("ces")}
            segHistMode={segHistMode}
            toggleSegHist={toggleSegHist}
            hiddenLines={hiddenLines}
            setHiddenLines={setHiddenLines}
            toggleLine={toggleLine}
          />
        )}
      </div>
    );
  })();
}
